import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Eye,
  Upload,
  Loader2,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const STYLE_OPTIONS = [
  { id: "ART_DECO", label: "Art Deco" },
  { id: "CLASSIC", label: "Classic" },
  { id: "MODERN", label: "Modern" },
  { id: "NEOCLASSIC", label: "Neoclassic" },
];
const TYPE_OPTIONS = [
  { id: "INTERIOR", label: "Interior" },
  { id: "EXTERIOR", label: "Exterior" },
];

export default function VisionFeedback() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [description, setDescription] = useState("");
  const [simType, setSimType] = useState("INTERIOR");
  const [style, setStyle] = useState("MODERN");
  const [autoApply, setAutoApply] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Imagine prea mare (max 10 MB)");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const reset = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    setDescription("");
    setResult(null);
  };

  const submit = async () => {
    if (!file) {
      toast.error("Atașează un screenshot");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("description", description);
      fd.append("sim_type", simType);
      fd.append("style", style);
      fd.append("auto_apply", autoApply ? "true" : "false");
      const { data } = await api.post("/admin/feedback/vision", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.auto_applied) {
        toast.success(
          `Anchor pentru ${simType}/${style} a fost actualizat automat ✨`,
        );
      } else {
        toast.message("Sugestie generată — revizuiește mai jos.");
      }
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Eroare AI",
      );
    } finally {
      setBusy(false);
    }
  };

  const applyManually = async () => {
    if (!result?.suggested_anchor) return;
    setBusy(true);
    try {
      await api.put(`/admin/prompts/${simType}/${style}`, {
        anchor: result.suggested_anchor,
      });
      toast.success(`Anchor aplicat pentru ${simType}/${style}`);
      setResult({ ...result, auto_applied: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="vision-feedback">
      <div className="rounded-xl border border-white/10 bg-zinc-900/30 p-5">
        <h2 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
          <Eye className="text-emerald-400" size={20} />
          Vision Feedback — învățare rapidă cu screenshot
        </h2>
        <p className="text-sm text-zinc-400 leading-relaxed">
          Atașează un screenshot dintr-un render problematic + (opțional) o
          descriere scurtă. Gemini Vision identifică automat categoria
          problemei și propune un anchor îmbunătățit pentru combinația aleasă.
          Cu „aplică automat", anchor-ul se salvează direct dacă AI-ul are
          ≥60% încredere.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* LEFT — input */}
        <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide block mb-2">
              Screenshot render-ului problematic
            </label>
            {preview ? (
              <div className="relative rounded-lg overflow-hidden border border-white/10 bg-black">
                <img
                  src={preview}
                  alt="preview"
                  className="w-full max-h-[320px] object-contain"
                  data-testid="vision-preview"
                />
                <button
                  onClick={reset}
                  className="absolute top-2 right-2 bg-black/70 hover:bg-black px-2 py-1 rounded text-xs text-white border border-white/20"
                  data-testid="vision-reset"
                >
                  <RotateCcw size={12} className="inline mr-1" /> Schimbă
                </button>
              </div>
            ) : (
              <label
                htmlFor="vision-file"
                className="block border-2 border-dashed border-white/15 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-500/40 transition"
                data-testid="vision-dropzone"
              >
                <Upload className="mx-auto text-zinc-500 mb-2" size={28} />
                <p className="text-sm text-zinc-400">
                  Click pentru a încărca un screenshot
                </p>
                <p className="text-[11px] text-zinc-500 mt-1">PNG/JPG, max 10 MB</p>
                <input
                  id="vision-file"
                  type="file"
                  accept="image/*"
                  onChange={onPick}
                  className="hidden"
                  data-testid="vision-file-input"
                />
              </label>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide block mb-1.5">
                Tip
              </label>
              <Select value={simType} onValueChange={setSimType}>
                <SelectTrigger className="bg-zinc-950/60 border-white/10 text-white" data-testid="vision-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10">
                  {TYPE_OPTIONS.map((t) => (
                    <SelectItem key={t.id} value={t.id} className="text-white">
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide block mb-1.5">
                Stil
              </label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger className="bg-zinc-950/60 border-white/10 text-white" data-testid="vision-style">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10">
                  {STYLE_OPTIONS.map((s) => (
                    <SelectItem key={s.id} value={s.id} className="text-white">
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide block mb-1.5">
              Descriere problemă (opțional)
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: pereții laterali au fost mutați spre interior, fereastra arată diferit..."
              rows={3}
              className="bg-zinc-950/60 border-white/10 text-sm resize-none focus-visible:ring-emerald-500/40"
              data-testid="vision-description"
              maxLength={500}
            />
          </div>

          <label className="flex items-start gap-3 p-3 border border-white/10 bg-zinc-950/40 rounded-md cursor-pointer hover:border-white/20 transition">
            <input
              type="checkbox"
              checked={autoApply}
              onChange={(e) => setAutoApply(e.target.checked)}
              className="mt-0.5 accent-emerald-500"
              data-testid="vision-auto-apply"
            />
            <div className="flex-1">
              <div className="text-sm text-white font-medium">
                Aplică automat dacă confidence ≥ 60%
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                Anchor-ul nou se salvează direct, fără confirmare manuală.
              </div>
            </div>
          </label>

          <Button
            onClick={submit}
            disabled={busy || !file}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="vision-submit"
          >
            {busy ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Se analizează cu Gemini Vision...
              </>
            ) : (
              <>
                <Sparkles size={16} className="mr-2" />
                Analizează cu AI
              </>
            )}
          </Button>
        </div>

        {/* RIGHT — result */}
        <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5 space-y-4 min-h-[300px]">
          {!result && !busy && (
            <div className="h-full flex flex-col items-center justify-center text-center text-zinc-500 py-12">
              <Sparkles className="mb-3 opacity-40" size={40} />
              <p className="text-sm">Rezultatul AI va apărea aici.</p>
            </div>
          )}
          {busy && (
            <div className="h-full flex flex-col items-center justify-center py-12">
              <Loader2 className="animate-spin text-emerald-400 mb-3" size={32} />
              <p className="text-sm text-zinc-300">Analizează imaginea...</p>
            </div>
          )}
          {result && (
            <div className="space-y-4" data-testid="vision-result">
              <div className="flex items-center gap-2 flex-wrap">
                {result.auto_applied ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-medium">
                    <CheckCircle2 size={13} /> Anchor aplicat automat
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-orange-500/15 border border-orange-500/40 text-orange-300 text-xs font-medium">
                    <AlertCircle size={13} /> Sugestie — confirmă mai jos
                  </span>
                )}
                <span className="text-xs text-zinc-500">
                  Confidence: <strong className="text-white">{Math.round((result.confidence || 0) * 100)}%</strong>
                </span>
              </div>

              <div className="rounded-lg border border-white/10 bg-zinc-950/40 p-3">
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">
                  Categorie detectată
                </p>
                <p className="text-sm text-white font-medium">{result.detected_reason_label}</p>
              </div>

              {result.observation && (
                <div className="rounded-lg border border-white/10 bg-zinc-950/40 p-3">
                  <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">
                    Ce a observat AI-ul
                  </p>
                  <p className="text-sm text-zinc-300 leading-relaxed">{result.observation}</p>
                </div>
              )}

              <div className="rounded-lg border border-zinc-700 bg-zinc-900/50 p-3">
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">
                  Anchor curent
                </p>
                <p className="text-xs text-zinc-300 italic">"{result.current_anchor}"</p>
              </div>

              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3">
                <p className="text-[11px] text-emerald-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <Sparkles size={11} /> Anchor sugerat
                </p>
                <p className="text-sm text-white font-medium">"{result.suggested_anchor}"</p>
                {result.reasoning && (
                  <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
                    💡 {result.reasoning}
                  </p>
                )}
              </div>

              {!result.auto_applied && (
                <Button
                  onClick={applyManually}
                  disabled={busy}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="vision-apply-manual"
                >
                  Aplică sugestia pentru {simType}/{style}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
